import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { filter, Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { MENU_ITEMS } from '../core/config/menu.config';
import { MenuItem } from '../core/models/menu.model';
import { AuthService } from '../core/services/auth.service';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    ButtonModule,
    AvatarModule,
    BadgeModule,
    TooltipModule,
    TranslateModule,
  ],
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit, OnDestroy {
  readonly menu = MENU_ITEMS;

  isDesktop =
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true;
  sidebarCollapsed = false;
  mobileOpen = false;
  currentPageLabel = 'menu.dashboard';

  private mediaQuery: MediaQueryList | null =
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)') : null;
  private mediaListener?: (event: MediaQueryListEvent) => void;
  private routerSub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly translate: TranslateService,
  ) {}

  /** User display helpers — derived from auth signal */
  get userName(): string {
    return this.authService.currentUser()?.fullName ?? this.translate.instant('shell.fallbackUser');
  }

  get userRole(): string {
    const roles = this.authService.currentUser()?.roles ?? [];
    if (roles.length === 0) return '';
    // Strip ROLE_ prefix for display
    return roles.map((r) => r.replace(/^ROLE_/i, '')).join(', ');
  }

  get userInitials(): string {
    const name = this.userName;
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }

  toggleSidebar() {
    if (this.isDesktop) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    } else {
      this.mobileOpen = !this.mobileOpen;
    }
  }

  handleNavClick() {
    if (!this.isDesktop) {
      this.mobileOpen = false;
    }
  }

  logout() {
    this.authService.logout();
  }

  ngOnInit() {
    if (this.mediaQuery) {
      this.mediaListener = (event: MediaQueryListEvent) => {
        this.isDesktop = event.matches;
        if (event.matches) {
          this.mobileOpen = false;
        } else {
          this.sidebarCollapsed = false;
        }
      };
      this.mediaQuery.addEventListener('change', this.mediaListener);
    }

    this.updateBreadcrumbFromUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.updateBreadcrumbFromUrl(event.urlAfterRedirects));
  }

  ngOnDestroy() {
    if (this.mediaQuery && this.mediaListener) {
      this.mediaQuery.removeEventListener('change', this.mediaListener);
    }
    this.routerSub?.unsubscribe();
  }

  private updateBreadcrumbFromUrl(url: string) {
    const segment = url.split('/').filter(Boolean)[0] ?? 'dashboard';
    const match = this.menu.find((item) => (item.path ?? '').replace('/', '') === segment);
    this.currentPageLabel = match?.label ?? 'menu.dashboard';
  }

  get collapseCompact() {
    return this.isDesktop && this.sidebarCollapsed;
  }
  get asideWidth() {
    return this.isDesktop ? (this.sidebarCollapsed ? 72 : 256) : 256;
  }
  get contentMargin() {
    return this.isDesktop ? (this.sidebarCollapsed ? 72 : 256) : 0;
  }
  get toggleExpanded() {
    return this.isDesktop ? !this.sidebarCollapsed : this.mobileOpen;
  }
}
